import { Dices } from "lucide-react";
import SpotifyIcon from "../assets/spotify.svg?react";
import { KALEIDOSCOPE_SOURCE_IMAGES } from "../lib/kaleidoscope-sources";
import { randomizeKaleidoscopeSettings } from "../lib/kaleidoscope-settings";
import type { KaleidoscopeSettings } from "../types/kaleidoscope";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Slider } from "./ui/slider";

interface KaleidoscopeSettingsPanelProps {
  settings: KaleidoscopeSettings;
  onChange: (settings: KaleidoscopeSettings) => void;
  canApplySpotifyColors?: boolean;
  onApplySpotifyColors?: () => void;
}

type KaleidoscopeSettingKey = keyof KaleidoscopeSettings;

const KALEIDOSCOPE_SECTION_KEYS = {
  palette: ["colors", "sourceImageIndex", "paletteIntensity", "colorSeparation", "darkLightBalance"],
  structure: ["segments", "facetDensity", "patternComplexity", "centerStructure", "outerReflection", "layerDepth"],
  motion: ["rotationSpeed", "morphSpeed", "shimmerSpeed", "pulseStrength", "driftAmount"],
  light: ["glowStrength", "bloomRadius", "highlightIntensity", "refractionStrength", "contrast"],
  detail: ["sparkleDensity", "microFacetStrength", "textureAmount"],
} satisfies Record<string, KaleidoscopeSettingKey[]>;

function pickKaleidoscopeSettings(
  source: KaleidoscopeSettings,
  keys: readonly KaleidoscopeSettingKey[]
): Partial<KaleidoscopeSettings> {
  return keys.reduce<Partial<KaleidoscopeSettings>>((accumulator, key) => {
    accumulator[key] = source[key];
    return accumulator;
  }, {});
}

function formatValue(value: number) {
  return Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.00$/, "").replace(/0$/, "");
}

function SettingsSection({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-white">{title}</h4>
          {description ? <p className="mt-1 text-xs text-white/60">{description}</p> : null}
        </div>
        {action}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

function SectionRandomButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      className="size-8 cursor-pointer border-white/20 bg-transparent text-white hover:bg-white/10"
      title="Randomize section"
      onClick={onClick}
    >
      <Dices className="size-4" />
    </Button>
  );
}

function SliderField({
  id,
  label,
  value,
  min,
  max,
  step,
  onChange,
}: {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}) {
  return (
    <div>
      <Label htmlFor={id} className="mb-2 block">
        {label} <span className="text-gray-400">({formatValue(value)})</span>
      </Label>
      <Slider
        id={id}
        min={min}
        max={max}
        step={step}
        value={[value]}
        onValueChange={([next]) => onChange(next)}
        className="w-full"
      />
    </div>
  );
}

export function KaleidoscopeSettingsPanel({
  settings,
  onChange,
  canApplySpotifyColors = false,
  onApplySpotifyColors,
}: KaleidoscopeSettingsPanelProps) {
  const randomizeSection = (keys: readonly KaleidoscopeSettingKey[]) => {
    const randomized = randomizeKaleidoscopeSettings();
    onChange({
      ...settings,
      ...pickKaleidoscopeSettings(randomized, keys),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-lg font-semibold">🔮 Kaleidoscope Settings</h3>

      <SettingsSection
        title="Color"
        description="Shape jewel-tone regions and highlight contrast without drifting into harshness."
        action={<SectionRandomButton onClick={() => randomizeSection(KALEIDOSCOPE_SECTION_KEYS.palette)} />}
      >
        <div>
          <div className="mb-2 flex items-center justify-between gap-2">
            <Label className="block">
              <b>Colors:</b>
            </Label>
            {canApplySpotifyColors && onApplySpotifyColors ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="cursor-pointer gap-2 border-white/20 bg-transparent text-white hover:bg-white/10"
                title="Apply colors from Spotify"
                onClick={onApplySpotifyColors}
              >
                <SpotifyIcon className="size-4" />
                Spotify Colors
              </Button>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {settings.colors.map((color, index) => (
              <Input
                key={index}
                type="color"
                value={color}
                className="h-10 w-20 cursor-pointer rounded border-none"
                onChange={(event) => {
                  const colors = [...settings.colors];
                  colors[index] = event.target.value;
                  onChange({ ...settings, colors });
                }}
              />
            ))}
          </div>
        </div>
        <div>
          <Label className="mb-2 block">
            <b>Source Image:</b>
          </Label>
          <div className="grid grid-cols-2 gap-2">
            {KALEIDOSCOPE_SOURCE_IMAGES.map((source, index) => (
              <Button
                key={source.id}
                type="button"
                variant={settings.sourceImageIndex === index ? "default" : "outline"}
                size="sm"
                className="min-w-0 cursor-pointer justify-start border-white/20 bg-transparent px-2 text-xs text-white hover:bg-white/10 data-[active=true]:bg-white data-[active=true]:text-black"
                data-active={settings.sourceImageIndex === index}
                title={source.label}
                onClick={() => onChange({ ...settings, sourceImageIndex: index })}
              >
                <span
                  className="size-4 shrink-0 rounded-sm border border-white/30 bg-cover bg-center"
                  style={{ backgroundImage: `url(${source.path})` }}
                />
                <span className="truncate">{source.label}</span>
              </Button>
            ))}
          </div>
        </div>
        <SliderField
          id="paletteIntensity"
          label="Palette Intensity"
          value={settings.paletteIntensity}
          min={0.35}
          max={1.4}
          step={0.05}
          onChange={(value) => onChange({ ...settings, paletteIntensity: value })}
        />
        <SliderField
          id="colorSeparation"
          label="Color Separation"
          value={settings.colorSeparation}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, colorSeparation: value })}
        />
        <SliderField
          id="darkLightBalance"
          label="Dark / Light Balance"
          value={settings.darkLightBalance}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, darkLightBalance: value })}
        />
      </SettingsSection>

      <SettingsSection
        title="Structure"
        description="Define the mirror system, faceted geometry, and how crystalline the center and edges feel."
        action={<SectionRandomButton onClick={() => randomizeSection(KALEIDOSCOPE_SECTION_KEYS.structure)} />}
      >
        <SliderField
          id="segments"
          label="Segments"
          value={settings.segments}
          min={6}
          max={18}
          step={2}
          onChange={(value) => onChange({ ...settings, segments: value })}
        />
        <SliderField
          id="facetDensity"
          label="Facet Density"
          value={settings.facetDensity}
          min={0.2}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, facetDensity: value })}
        />
        <SliderField
          id="patternComplexity"
          label="Pattern Complexity"
          value={settings.patternComplexity}
          min={0.2}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, patternComplexity: value })}
        />
        <SliderField
          id="centerStructure"
          label="Center Structure"
          value={settings.centerStructure}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, centerStructure: value })}
        />
        <SliderField
          id="outerReflection"
          label="Outer Reflection"
          value={settings.outerReflection}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, outerReflection: value })}
        />
        <SliderField
          id="layerDepth"
          label="Layer Depth"
          value={settings.layerDepth}
          min={0.1}
          max={1.1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, layerDepth: value })}
        />
      </SettingsSection>

      <SettingsSection
        title="Motion"
        description="Keep movement meditative while letting reflections and highlights slowly shimmer."
        action={<SectionRandomButton onClick={() => randomizeSection(KALEIDOSCOPE_SECTION_KEYS.motion)} />}
      >
        <SliderField
          id="rotationSpeed"
          label="Rotation Speed"
          value={settings.rotationSpeed}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, rotationSpeed: value })}
        />
        <SliderField
          id="morphSpeed"
          label="Morph Speed"
          value={settings.morphSpeed}
          min={0}
          max={1.1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, morphSpeed: value })}
        />
        <SliderField
          id="driftAmount"
          label="Drift Amount"
          value={settings.driftAmount}
          min={0}
          max={1.1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, driftAmount: value })}
        />
        <SliderField
          id="pulseStrength"
          label="Pulse Strength"
          value={settings.pulseStrength}
          min={0}
          max={0.9}
          step={0.05}
          onChange={(value) => onChange({ ...settings, pulseStrength: value })}
        />
        <SliderField
          id="shimmerSpeed"
          label="Shimmer Speed"
          value={settings.shimmerSpeed}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, shimmerSpeed: value })}
        />
      </SettingsSection>

      <SettingsSection
        title="Light"
        description="Keep geometry readable, then add restrained prism glow and highlight polish." 
        action={<SectionRandomButton onClick={() => randomizeSection(KALEIDOSCOPE_SECTION_KEYS.light)} />}
      >
        <SliderField
          id="glowStrength"
          label="Glow Strength"
          value={settings.glowStrength}
          min={0.2}
          max={1.4}
          step={0.05}
          onChange={(value) => onChange({ ...settings, glowStrength: value })}
        />
        <SliderField
          id="bloomRadius"
          label="Bloom Radius"
          value={settings.bloomRadius}
          min={0.08}
          max={0.7}
          step={0.05}
          onChange={(value) => onChange({ ...settings, bloomRadius: value })}
        />
        <SliderField
          id="highlightIntensity"
          label="Highlight Intensity"
          value={settings.highlightIntensity}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, highlightIntensity: value })}
        />
        <SliderField
          id="refractionStrength"
          label="Refraction Strength"
          value={settings.refractionStrength}
          min={0}
          max={0.8}
          step={0.05}
          onChange={(value) => onChange({ ...settings, refractionStrength: value })}
        />
        <SliderField
          id="contrast"
          label="Contrast"
          value={settings.contrast}
          min={0.2}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, contrast: value })}
        />
      </SettingsSection>

      <SettingsSection
        title="Detail"
        description="Fill the frame with multi-scale shards, reflections, and mirrored structure without clutter."
        action={<SectionRandomButton onClick={() => randomizeSection(KALEIDOSCOPE_SECTION_KEYS.detail)} />}
      >
        <SliderField
          id="sparkleDensity"
          label="Sparkle Density"
          value={settings.sparkleDensity}
          min={0}
          max={1}
          step={0.05}
          onChange={(value) => onChange({ ...settings, sparkleDensity: value })}
        />
        <SliderField
          id="microFacetStrength"
          label="Micro Facet Strength"
          value={settings.microFacetStrength}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, microFacetStrength: value })}
        />
        <SliderField
          id="textureAmount"
          label="Texture Amount"
          value={settings.textureAmount}
          min={0}
          max={1.2}
          step={0.05}
          onChange={(value) => onChange({ ...settings, textureAmount: value })}
        />
      </SettingsSection>
    </div>
  );
}
